// One word diff per alignment pair, computed once and read twice.
//
// The two columns of Review are two views of the same comparison: the
// Manuscript side shows what Refinement took out, the Narration Script side
// shows what it put in. Computing the diff once per pair and deriving both
// sides from it is what keeps them honest, and it is why a row that scrolls
// back into view costs a cache lookup rather than a second diff.

import type { AlignedNode, AlignmentPair } from "@contract";
import { type Change, diffWordsWithSpace } from "diff";
import type { Range } from "./spans";

export interface PairDiff {
  /** Both sides interleaved, for the inline view. */
  readonly parts: readonly Change[];
  /** Only what the Narration Script has: the unchanged runs and the insertions. */
  readonly scriptParts: readonly Change[];
  /** What the Script dropped, in `strippedSourceText` coordinates, contiguous runs merged. */
  readonly removals: readonly Range[];
}

/** A gap that is only spaces and line breaks sits inside a run rather than breaking it. */
const isGap = (value: string): boolean => value.trim() === "";

const sameKind = (a: Change, b: Change): boolean =>
  Boolean(a.added) === Boolean(b.added) && Boolean(a.removed) === Boolean(b.removed);

/**
 * Joins parts that say the same thing into one run.
 *
 * `absorbGaps` decides whether an unchanged whitespace part between two parts
 * of one kind is swallowed into the run. It must stay off for the interleaved
 * parts: that whitespace belongs to both sides at once, and moving it into a
 * removal would take it off the Narration Script, which is the side the
 * offsets in `nodeSpans` are measured against. It is on for the script-only
 * parts, where there is nothing to take it away from.
 */
function coalesce(parts: readonly Change[], absorbGaps = false): Change[] {
  const merged: Change[] = [];
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i] as Change;
    const last = merged[merged.length - 1];
    if (last && sameKind(last, part)) {
      merged[merged.length - 1] = { ...last, value: last.value + part.value };
      continue;
    }
    const next = parts[i + 1];
    const bridgesARun =
      absorbGaps &&
      last !== undefined &&
      next !== undefined &&
      !part.added &&
      !part.removed &&
      isGap(part.value) &&
      sameKind(last, next) &&
      (next.added === true || next.removed === true);
    if (last && bridgesARun) {
      merged[merged.length - 1] = { ...last, value: last.value + part.value };
      continue;
    }
    merged.push({ ...part });
  }
  return merged;
}

/**
 * Walks the parts once, collecting the removed ranges on the source side.
 * Ranges separated by nothing but whitespace become one range: three dropped
 * words are one dropped phrase to the Author, and painting them as three
 * elements would put a "removed" announcement between every pair of them.
 */
function removalsOf(parts: readonly Change[], source: string): Range[] {
  const removals: Range[] = [];
  let at = 0;
  for (const part of parts) {
    if (part.added) continue;
    const end = at + part.value.length;
    if (part.removed) {
      const last = removals[removals.length - 1];
      if (last && isGap(source.slice(last.end, at))) {
        removals[removals.length - 1] = { start: last.start, end };
      } else {
        removals.push({ start: at, end });
      }
    }
    at = end;
  }
  return removals;
}

/**
 * How many diffs are kept. A Chapter of 1,200 Manuscript nodes packs to under
 * a hundred Blocks, so this holds several whole versions of the largest
 * Chapter the mock offers and still cannot grow without bound while an Author
 * edits their way through a Book in one session.
 */
const CACHE_LIMIT = 2000;

const cache = new Map<string, PairDiff>();

/**
 * The key an edit has to change: a forked version mints new Block ids, an
 * in-place edit changes the text. The separator is written as an escape
 * sequence, never as a literal NUL: a source file carrying one is stored as
 * binary by git, and every future diff and review of this module would be
 * blank.
 */
const cacheKey = (pair: AlignmentPair): string =>
  `${pair.block.id}\u0000${pair.strippedSourceText.length}\u0000${pair.block.text}`;

/**
 * The diff of one pair, memoized. Reading refreshes the entry's recency, so a
 * long scroll through a big Chapter evicts the rows nobody came back to
 * rather than the opening the Author keeps returning to.
 */
export function pairDiff(pair: AlignmentPair): PairDiff {
  const key = cacheKey(pair);
  const hit = cache.get(key);
  if (hit) {
    cache.delete(key);
    cache.set(key, hit);
    return hit;
  }
  // diffWordsWithSpace, not diffWords: this is still a word-level diff, but it
  // keeps whitespace as its own parts. jsdiff's `diffWords` folds whitespace
  // into the tokens, so concatenating the unchanged and removed parts no longer
  // reproduces the source, and every offset the walk below derives would drift
  // by a few characters over a long paragraph. Exact offsets are what let the
  // Manuscript column paint these removals over text it renders from the nodes.
  const parts = coalesce(diffWordsWithSpace(pair.strippedSourceText, pair.block.text));
  // The script column gets its side filtered first and coalesced after, so a
  // whole inserted sentence is one element with one screen-reader
  // announcement, rather than one per word with a removal from the other side
  // between each pair of them.
  const computed: PairDiff = {
    parts,
    scriptParts: coalesce(
      parts.filter((part) => !part.removed),
      true,
    ),
    removals: removalsOf(parts, pair.strippedSourceText),
  };
  cache.set(key, computed);
  if (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next();
    if (!oldest.done) cache.delete(oldest.value);
  }
  return computed;
}

/** Empties the cache; a test that measures work has to start from a known state. */
export function clearDiffCache(): void {
  cache.clear();
}

/** How many diffs are held, so a test can prove the second read of a row is free. */
export const diffCacheSize = (): number => cache.size;

/** A node's place in the pair's `strippedSourceText`, or null when it contributed nothing to it. */
export interface NodeSpan {
  readonly node: AlignedNode;
  readonly span: Range | null;
}

/**
 * Where each node's text sits inside the joined source, found by matching
 * rather than by rebuilding the join. The server decides which nodes reach
 * `strippedSourceText` (page furniture and nodes that refined to nothing are
 * left out of it but stay in `nodes`, because they are still part of what the
 * Block was made from). Re-deriving that rule here would be a second
 * implementation of it that could disagree; walking the string cannot. A node
 * the walk does not find simply carries no removals, which is the safe way to
 * be wrong.
 */
export function nodeSpans(pair: AlignmentPair): NodeSpan[] {
  const source = pair.strippedSourceText;
  let at = 0;
  return pair.nodes.map((node) => {
    const text = node.strippedText;
    if (text === "" || !source.startsWith(text, at)) return { node, span: null };
    const span: Range = { start: at, end: at + text.length };
    at = span.end;
    if (source.startsWith("\n\n", at)) at += 2;
    return { node, span };
  });
}
